import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { UseFormSetError } from "react-hook-form";
import { t } from "i18next";

import { DOMAIN_AUTH } from "../domain";

import { ResetPasswordFormValues } from "@components/form";

export const ACTION_RESET_PASSWORD = "RESET_PASSWORD";

interface ResetPasswordApiError {
  message?: string[];
  error?: string;
  statusCode?: number;
}

const resetPassword = async (params: {
  token: string;
  password: string;
  confirmPassword: string;
}): Promise<void> => {
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/reset-password`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: params.token,
        password: params.password,
        confirmPassword: params.confirmPassword,
      }),
    },
  );
  const errorBody: ResetPasswordApiError = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw {
      name: "ResetPasswordError",
      message: "Failed to reset password",
      response: errorBody,
      status: response.status,
    };
  }
};

interface UseResetPasswordOptions {
  setError?: UseFormSetError<ResetPasswordFormValues>;
  onExpired?: () => void;
}

export const useResetPassword = (
  options?: UseResetPasswordOptions,
): {
  resetPassword: UseMutateFunction<
    void,
    { response?: ResetPasswordApiError; message: string },
    { token: string; password: string; confirmPassword: string },
    unknown
  >;
  isSuccessResetPassword: boolean;
  isPendingResetPassword: boolean;
} => {
  const queryClient = useQueryClient();

  type ResetPasswordError = {
    response?: {
      message?: string[];
      error?: string;
      statusCode?: number;
    };
    message: string;
  };

  const { mutate, isSuccess, isPending } = useMutation<
    void,
    ResetPasswordError,
    { token: string; password: string; confirmPassword: string }
  >({
    mutationKey: [DOMAIN_AUTH, ACTION_RESET_PASSWORD],
    mutationFn: resetPassword,
    onError: (error) => {
      const messages = error.response?.message;

      if (options?.setError) {
        if (Array.isArray(messages) && messages.includes("passwords_dont_match")) {
          options.setError("confirmPassword", {
            type: "server",
            message: t("auth.passwordsDontMatch"),
          });
        } else if (
          Array.isArray(messages) &&
          messages.some((m) =>
            [
              "password_not_secure",
              "password_invalid",
              "password_too_short",
              "password_too_long",
              "password_required",
            ].includes(m),
          )
        ) {
          options.setError("password", {
            type: "server",
            message: t("auth.invalidPassword"),
          });
        } else {
          options?.onExpired?.();
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DOMAIN_AUTH] });
    },
  });

  return {
    resetPassword: mutate,
    isSuccessResetPassword: isSuccess,
    isPendingResetPassword: isPending,
  };
};
